/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { usePushBackHandler } from '../context/BackStackContext'

export type MemberOverlayEntry =
  | { kind: 'node'; id: string }
  | { kind: 'checklist'; id: string; parentId?: string | null; initialOccurrenceKey?: string | null }

export interface MemberOverlayContextValue {
  stack: MemberOverlayEntry[]
  top: MemberOverlayEntry | null
  openNode: (id: string) => void
  openChecklist: (id: string, parentId?: string | null, initialOccurrenceKey?: string | null) => void
  closeTop: () => void
  closeAll: () => void
}

const EMPTY_STACK: MemberOverlayEntry[] = []
const noop = () => {}
const fallbackValue: MemberOverlayContextValue = {
  stack: EMPTY_STACK,
  top: null,
  openNode: noop,
  openChecklist: noop,
  closeTop: noop,
  closeAll: noop,
}

const MemberOverlayContext = createContext<MemberOverlayContextValue | null>(null)

const MemberOverlayBackHandler = ({ onBack }: { onBack: () => void }) => {
  usePushBackHandler(onBack, true)
  return null
}

const pushEntry = (stack: MemberOverlayEntry[], entry: MemberOverlayEntry): MemberOverlayEntry[] => {
  const top = stack.at(-1)
  if (top?.kind === entry.kind && top.id === entry.id) return stack
  if (stack.length < 4) return [...stack, entry]
  return [...stack.slice(0, -1), entry]
}

export const MemberOverlayProvider = ({ children }: { children: ReactNode }) => {
  const [stack, setStack] = useState<MemberOverlayEntry[]>([])

  const openNode = useCallback((id: string) => {
    setStack((current) => pushEntry(current, { kind: 'node', id }))
  }, [])

  const openChecklist = useCallback((id: string, parentId?: string | null, initialOccurrenceKey?: string | null) => {
    setStack((current) => pushEntry(current, { kind: 'checklist', id, parentId, initialOccurrenceKey }))
  }, [])

  const closeTop = useCallback(() => {
    setStack((current) => current.length ? current.slice(0, -1) : current)
  }, [])

  const closeAll = useCallback(() => setStack(EMPTY_STACK), [])
  const top = stack.at(-1) ?? null
  const value = useMemo<MemberOverlayContextValue>(() => ({
    stack,
    top,
    openNode,
    openChecklist,
    closeTop,
    closeAll,
  }), [closeAll, closeTop, openChecklist, openNode, stack, top])

  return (
    <MemberOverlayContext.Provider value={value}>
      {children}
      {stack.map((entry, index) => (
        <MemberOverlayBackHandler
          key={`${entry.kind}:${entry.id}:${index}`}
          onBack={closeTop}
        />
      ))}
    </MemberOverlayContext.Provider>
  )
}

export const useMemberOverlay = (): MemberOverlayContextValue => useContext(MemberOverlayContext) ?? fallbackValue
